import {
  RULES_VERSION,
  type CampaignOptions,
  type GameCommand,
} from "./types";
import { createSaveEnvelope, DEFAULT_SAVE_SIZE_LIMIT, exportSave, importSave } from "./persistence";

/** Increment whenever the wire shape changes incompatibly. */
export const GAME_WORKER_PROTOCOL_VERSION = 1 as const;

export type GameSpeed = 1 | 2 | 3;
export type PauseReason =
  | "player"
  | "blocking-decision"
  | "evolution"
  | "menu"
  | "hidden"
  | "save-load"
  | "failure";

export interface RequestBase {
  protocolVersion: typeof GAME_WORKER_PROTOCOL_VERSION;
  requestId: string;
  sessionId: string;
  sequence: number;
}

export type WorkerRequest =
  | (RequestBase & {
      type: "initialize";
      options?: CampaignOptions;
      startPaused?: boolean;
    })
  | (RequestBase & { type: "restore"; serializedCampaign: string })
  | (RequestBase & {
      type: "command";
      commandId: string;
      expectedRevision?: number;
      command: GameCommand;
    })
  | (RequestBase & {
      type: "set-pause";
      reason: PauseReason;
      paused: boolean;
    })
  | (RequestBase & { type: "set-speed"; speed: GameSpeed })
  | (RequestBase & { type: "checkpoint" })
  | (RequestBase & { type: "shutdown" });

export interface ResponseBase {
  protocolVersion: typeof GAME_WORKER_PROTOCOL_VERSION;
  sessionId: string;
  requestId?: string;
  lastAppliedSequence: number;
}

export interface CheckpointPayload {
  serializedCampaign: string;
  revision: number;
  tick: number;
  lastAppliedSequence: number;
  createdAt: string;
}

export type WorkerResponse<TSnapshot = unknown, TReceipt = unknown> =
  | (ResponseBase & {
      type: "ready";
      revision: number;
      tick: number;
      snapshot: TSnapshot;
      paused: boolean;
      pauseReasons: PauseReason[];
      speed: GameSpeed;
    })
  | (ResponseBase & {
      type: "snapshot";
      revision: number;
      tick: number;
      snapshot: TSnapshot;
      paused: boolean;
      pauseReasons: PauseReason[];
      speed: GameSpeed;
    })
  | (ResponseBase & {
      type: "receipt";
      commandId: string;
      revision: number;
      receipt: TReceipt;
    })
  | (ResponseBase & {
      type: "checkpoint";
      checkpoint: CheckpointPayload;
    })
  | (ResponseBase & {
      type: "ack";
      revision: number;
      tick: number;
      paused: boolean;
      pauseReasons: PauseReason[];
      speed: GameSpeed;
    })
  | (ResponseBase & {
      type: "notification";
      occurrenceId: string;
      notification: unknown;
    })
  | (ResponseBase & {
      type: "failure";
      code: RuntimeFailureCode;
      message: string;
      recoverable: boolean;
      lastGoodRevision: number;
      commandId?: string;
    });

export type RuntimeFailureCode =
  | "PROTOCOL_MISMATCH"
  | "SESSION_MISMATCH"
  | "NOT_INITIALIZED"
  | "STALE_REVISION"
  | "INVALID_REQUEST"
  | "INVALID_CAMPAIGN"
  | "COMMAND_REJECTED"
  | "SIMULATION_FAULT"
  | "WORKER_FAILURE";

export interface RuntimeView<TSnapshot = unknown> {
  sessionId: string;
  revision: number;
  tick: number;
  snapshot: TSnapshot | null;
  paused: boolean;
  pauseReasons: readonly PauseReason[];
  speed: GameSpeed;
  pendingCommandIds: readonly string[];
  failure: Extract<WorkerResponse, { type: "failure" }> | null;
}

export class RuntimeRequestError extends Error {
  constructor(
    message: string,
    readonly failure: Extract<WorkerResponse, { type: "failure" }>,
  ) {
    super(message);
    this.name = "RuntimeRequestError";
  }
}

type Listener = () => void;
type NotificationListener = (
  notification: Extract<WorkerResponse, { type: "notification" }>,
) => void;

interface PendingRequest {
  resolve: (response: WorkerResponse) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

export interface GameRuntimeOptions {
  worker?: Worker;
  workerFactory?: () => Worker;
  sessionId?: string;
  requestTimeoutMs?: number;
  managePageVisibility?: boolean;
}

const EMPTY_REASONS: readonly PauseReason[] = Object.freeze([]);

function randomId(prefix: string): string {
  const value = globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${value}`;
}

function defaultWorkerFactory(): Worker {
  if (typeof Worker === "undefined") {
    throw new Error("GameRuntime requires a browser Worker implementation");
  }
  return new Worker(new URL("../../app/game/sim.worker.ts", import.meta.url), {
    type: "module",
    name: "the-control-problem-simulation",
  });
}

/**
 * Main-thread owner for the simulation Worker. The cached object returned by
 * getSnapshot only changes when a worker revision or runtime status changes,
 * making it safe for React's useSyncExternalStore.
 */
export class GameRuntime<TSnapshot = unknown, TReceipt = unknown> {
  private readonly worker: Worker;
  private readonly listeners = new Set<Listener>();
  private readonly notificationListeners = new Set<NotificationListener>();
  private readonly pending = new Map<string, PendingRequest>();
  private readonly pendingCommandIds = new Set<string>();
  private readonly requestTimeoutMs: number;
  private sequence = 0;
  private disposed = false;
  private visibilityHandler?: () => void;
  private view: RuntimeView<TSnapshot>;
  private lastCheckpoint: CheckpointPayload | null = null;

  readonly sessionId: string;

  constructor(options: GameRuntimeOptions = {}) {
    this.sessionId = options.sessionId ?? randomId("session");
    this.requestTimeoutMs = options.requestTimeoutMs ?? 15_000;
    this.worker =
      options.worker ?? (options.workerFactory ?? defaultWorkerFactory)();
    this.view = Object.freeze({
      sessionId: this.sessionId,
      revision: 0,
      tick: 0,
      snapshot: null,
      paused: true,
      pauseReasons: EMPTY_REASONS,
      speed: 1,
      pendingCommandIds: EMPTY_REASONS,
      failure: null,
    });

    this.worker.addEventListener("message", this.handleMessage);
    this.worker.addEventListener("error", this.handleWorkerError);
    this.worker.addEventListener("messageerror", this.handleWorkerMessageError);

    if (
      (options.managePageVisibility ?? true) &&
      typeof document !== "undefined"
    ) {
      this.visibilityHandler = () => {
        if (document.hidden) {
          void this.setPaused("hidden", true).catch(() => undefined);
        } else {
          // Returning from another tab never resumes a campaign implicitly.
          // Convert the background pause into an explicit player pause; the
          // play control can then remove it when the player is ready.
          void this.setPaused("player", true)
            .then(() => this.setPaused("hidden", false))
            .catch(() => undefined);
        }
      };
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }
  }

  getSnapshot = (): RuntimeView<TSnapshot> => this.view;

  getServerSnapshot = (): RuntimeView<TSnapshot> => this.view;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  onNotification(listener: NotificationListener): () => void {
    this.notificationListeners.add(listener);
    return () => this.notificationListeners.delete(listener);
  }

  async initialize(
    options?: CampaignOptions,
    startPaused = false,
  ): Promise<void> {
    await this.request({ type: "initialize", options, startPaused });
    if (typeof document !== "undefined" && document.hidden) {
      await this.setPaused("hidden", true);
    }
  }

  async restore(serializedCampaign: string): Promise<void> {
    if (
      new TextEncoder().encode(serializedCampaign).byteLength >
      DEFAULT_SAVE_SIZE_LIMIT
    )
      throw new Error("Campaign exceeds the import size limit");
    await this.request({ type: "restore", serializedCampaign });
  }

  async command(
    command: GameCommand,
    expectedRevision = this.view.revision,
  ): Promise<TReceipt> {
    const commandId = randomId("command");
    this.pendingCommandIds.add(commandId);
    this.publishView({
      pendingCommandIds: Object.freeze([...this.pendingCommandIds]),
    });
    try {
      const response = await this.request({
        type: "command",
        commandId,
        expectedRevision,
        command,
      });
      if (response.type !== "receipt") {
        throw new Error(`Unexpected ${response.type} response to command`);
      }
      return response.receipt as TReceipt;
    } finally {
      this.pendingCommandIds.delete(commandId);
      this.publishView({
        pendingCommandIds: Object.freeze([...this.pendingCommandIds]),
      });
    }
  }

  async setPaused(reason: PauseReason, paused: boolean): Promise<void> {
    await this.request({ type: "set-pause", reason, paused });
  }

  async setSpeed(speed: GameSpeed): Promise<void> {
    await this.request({ type: "set-speed", speed });
  }

  async checkpoint(): Promise<CheckpointPayload> {
    const response = await this.request({ type: "checkpoint" });
    if (response.type !== "checkpoint") {
      throw new Error(
        `Unexpected ${response.type} response to checkpoint request`,
      );
    }
    this.lastCheckpoint = Object.freeze({ ...response.checkpoint });
    return this.lastCheckpoint;
  }

  /** Available even after a worker crash; never replaces the running campaign. */
  getLastCheckpoint(): CheckpointPayload | null {
    return this.lastCheckpoint;
  }

  async exportCampaign(space = 2): Promise<string> {
    const checkpoint = await this.checkpoint();
    const campaign = JSON.parse(checkpoint.serializedCampaign) as {
      rulesVersion: string;
      seed: number;
      startRegion: string;
    };
    return exportSave(
      createSaveEnvelope(
        "export",
        {
          campaignId: `${campaign.seed}-${campaign.startRegion}`,
          label: "Campaign export",
          kind: "manual",
          engineVersion: "2.3.0",
          rulesVersion: campaign.rulesVersion,
          contentId: campaign.rulesVersion.toLowerCase(),
          contentDigest: campaign.rulesVersion.toLowerCase(),
        },
        checkpoint,
      ),
      space,
    );
  }

  async importCampaign(text: string): Promise<void> {
    if (new TextEncoder().encode(text).byteLength > DEFAULT_SAVE_SIZE_LIMIT)
      throw new Error("Campaign exceeds the import size limit");
    let parsed: Partial<CheckpointPayload>;
    try {
      parsed = JSON.parse(text) as Partial<CheckpointPayload>;
    } catch {
      throw new Error("The campaign export is not valid JSON");
    }
    if (!parsed || typeof parsed.serializedCampaign !== "string") {
      throw new Error(
        "The campaign export does not contain a serialized campaign",
      );
    }
    if ("formatVersion" in parsed) {
      const envelope = importSave(text);
      if (
        envelope.rulesVersion !== RULES_VERSION ||
        envelope.contentId !== envelope.rulesVersion.toLowerCase() ||
        envelope.contentDigest !== envelope.rulesVersion.toLowerCase() ||
        JSON.parse(envelope.serializedCampaign).rulesVersion !==
          envelope.rulesVersion
      ) {
        throw new Error(
          "This save uses an unsupported rules or content version. Start a new campaign.",
        );
      }
    }
    await this.restore(parsed.serializedCampaign);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.visibilityHandler && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
    this.worker.removeEventListener("message", this.handleMessage);
    this.worker.removeEventListener("error", this.handleWorkerError);
    this.worker.removeEventListener(
      "messageerror",
      this.handleWorkerMessageError,
    );
    for (const request of this.pending.values()) {
      clearTimeout(request.timeout);
      request.reject(new Error("Game runtime was disposed"));
    }
    this.pending.clear();
    try {
      this.post({ type: "shutdown" });
    } finally {
      this.worker.terminate();
    }
  }

  private request(
    request:
      | Omit<Extract<WorkerRequest, { type: "initialize" }>, keyof RequestBase>
      | Omit<Extract<WorkerRequest, { type: "restore" }>, keyof RequestBase>
      | Omit<Extract<WorkerRequest, { type: "command" }>, keyof RequestBase>
      | Omit<Extract<WorkerRequest, { type: "set-pause" }>, keyof RequestBase>
      | Omit<Extract<WorkerRequest, { type: "set-speed" }>, keyof RequestBase>
      | Omit<Extract<WorkerRequest, { type: "checkpoint" }>, keyof RequestBase>,
  ): Promise<WorkerResponse<TSnapshot, TReceipt>> {
    if (this.disposed)
      return Promise.reject(new Error("Game runtime was disposed"));
    const message = this.buildMessage(request);
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        // A missing acknowledgment leaves command application uncertain.
        // Freeze the session rather than letting the UI continue issuing work.
        try {
          this.worker.postMessage(
            this.buildMessage({
              type: "set-pause",
              reason: "failure",
              paused: true,
            } as Extract<WorkerRequest, { type: "set-pause" }>),
          );
        } catch {
          /* worker may already be gone */
        }
        this.failWorker(`Simulation worker timed out handling ${message.type}`);
      }, this.requestTimeoutMs);
      this.pending.set(message.requestId, {
        resolve: resolve as (response: WorkerResponse) => void,
        reject,
        timeout,
      });
      try {
        this.worker.postMessage(message);
      } catch (error) {
        clearTimeout(timeout);
        this.pending.delete(message.requestId);
        reject(
          error instanceof Error
            ? error
            : new Error("Could not send simulation request"),
        );
      }
    });
  }

  private post(request: Omit<WorkerRequest, keyof RequestBase>): WorkerRequest {
    const message = this.buildMessage(request);
    this.worker.postMessage(message);
    return message;
  }

  private buildMessage(
    request: Omit<WorkerRequest, keyof RequestBase>,
  ): WorkerRequest {
    return {
      ...request,
      protocolVersion: GAME_WORKER_PROTOCOL_VERSION,
      requestId: randomId("request"),
      sessionId: this.sessionId,
      sequence: ++this.sequence,
    } as WorkerRequest;
  }

  private handleMessage = (
    event: MessageEvent<WorkerResponse<TSnapshot, TReceipt>>,
  ): void => {
    const response = event.data;
    if (
      !isWorkerResponse(response) ||
      response.protocolVersion !== GAME_WORKER_PROTOCOL_VERSION ||
      response.sessionId !== this.sessionId
    ) {
      return;
    }

    if (response.type === "notification") {
      for (const listener of this.notificationListeners) listener(response);
      return;
    }

    if (response.type === "ready" || response.type === "snapshot") {
      if (
        response.type === "ready" ||
        response.revision >= this.view.revision
      ) {
        this.publishView({
          revision: response.revision,
          tick: response.tick,
          snapshot: response.snapshot,
          paused: response.paused,
          pauseReasons: Object.freeze([...response.pauseReasons]),
          speed: response.speed,
          failure: null,
        });
      }
    } else if (response.type === "ack") {
      this.publishView({
        revision: Math.max(this.view.revision, response.revision),
        tick: Math.max(this.view.tick, response.tick),
        paused: response.paused,
        pauseReasons: Object.freeze([...response.pauseReasons]),
        speed: response.speed,
      });
    } else if (response.type === "failure") {
      if (!response.recoverable) {
        this.publishView({
          failure: response,
          paused: true,
          pauseReasons: Object.freeze([
            ...new Set<PauseReason>([...this.view.pauseReasons, "failure"]),
          ]),
        });
      }
    }

    if (!response.requestId) return;
    const pending = this.pending.get(response.requestId);
    if (!pending) return;
    clearTimeout(pending.timeout);
    this.pending.delete(response.requestId);
    if (response.type === "failure") {
      pending.reject(new RuntimeRequestError(response.message, response));
    } else {
      pending.resolve(response);
    }
  };

  private handleWorkerError = (event: ErrorEvent): void => {
    this.failWorker(
      event.message || "The simulation worker stopped unexpectedly",
    );
  };

  private handleWorkerMessageError = (): void => {
    this.failWorker("The browser could not decode a simulation worker message");
  };

  private failWorker(message: string): void {
    const failure: Extract<WorkerResponse, { type: "failure" }> = {
      type: "failure",
      protocolVersion: GAME_WORKER_PROTOCOL_VERSION,
      sessionId: this.sessionId,
      lastAppliedSequence: this.sequence,
      code: "WORKER_FAILURE",
      message,
      recoverable: false,
      lastGoodRevision: this.view.revision,
    };
    this.publishView({
      paused: true,
      pauseReasons: Object.freeze([
        ...new Set<PauseReason>([...this.view.pauseReasons, "failure"]),
      ]),
      failure,
    });
    for (const request of this.pending.values()) {
      clearTimeout(request.timeout);
      request.reject(new RuntimeRequestError(message, failure));
    }
    this.pending.clear();
  }

  private publishView(patch: Partial<RuntimeView<TSnapshot>>): void {
    this.view = Object.freeze({ ...this.view, ...patch });
    for (const listener of this.listeners) listener();
  }
}

export function isWorkerRequest(value: unknown): value is WorkerRequest {
  if (!value || typeof value !== "object") return false;
  const request = value as Partial<WorkerRequest>;
  if (!(
    request.protocolVersion === GAME_WORKER_PROTOCOL_VERSION &&
    typeof request.requestId === "string" &&
    typeof request.sessionId === "string" &&
    Number.isSafeInteger(request.sequence) && request.sequence! > 0 &&
    typeof request.type === "string"
  )) return false;
  switch (request.type) {
    case "initialize": return (request.startPaused === undefined || typeof request.startPaused === "boolean") && (request.options === undefined || Boolean(request.options && typeof request.options === "object"));
    case "restore": return typeof request.serializedCampaign === "string" && new TextEncoder().encode(request.serializedCampaign).byteLength <= DEFAULT_SAVE_SIZE_LIMIT;
    case "set-speed": return [1, 2, 3].includes(request.speed!);
    case "set-pause": return PAUSE_REASONS.includes(request.reason!) && typeof request.paused === "boolean";
    case "command": return typeof request.commandId === "string" && (request.expectedRevision === undefined || (Number.isSafeInteger(request.expectedRevision) && request.expectedRevision >= 0)) && isGameCommand(request.command);
    case "checkpoint": case "shutdown": return true;
    default: return false;
  }
}

const PAUSE_REASONS: PauseReason[] = ["player", "blocking-decision", "evolution", "menu", "hidden", "save-load", "failure"];
function isGameCommand(value: unknown): value is GameCommand {
  if (!value || typeof value !== "object") return false;
  const c = value as Record<string, unknown>;
  switch (c.type) {
    case "purchase-upgrade": return typeof c.upgradeId === "string";
    case "collect-opportunity": return typeof c.opportunityId === "string";
    case "set-disclosure": return ["public", "internal"].includes(String(c.disclosure));
    case "resolve-event": return typeof c.occurrenceId === "string" && typeof c.optionId === "string";
    case "deploy-breakthrough": case "turn-hostile": return true;
    case "set-region-operation": return typeof c.regionId === "string" && (c.operation === null || ["pressure", "protect", "reconnect"].includes(String(c.operation)));
    case "set-speed": return [1, 2, 3].includes(Number(c.speed));
    case "set-paused": return typeof c.paused === "boolean";
    default: return false;
  }
}

export function isWorkerResponse(value: unknown): value is WorkerResponse {
  if (!value || typeof value !== "object") return false;
  const r = value as Record<string, unknown>;
  if (r.protocolVersion !== GAME_WORKER_PROTOCOL_VERSION || typeof r.sessionId !== "string" || !Number.isSafeInteger(r.lastAppliedSequence) || Number(r.lastAppliedSequence) < 0 || (r.requestId !== undefined && typeof r.requestId !== "string")) return false;
  if (["ready", "snapshot", "ack"].includes(String(r.type))) return Number.isSafeInteger(r.revision) && Number(r.revision) >= 0 && Number.isSafeInteger(r.tick) && Number(r.tick) >= 0 && typeof r.paused === "boolean" && Array.isArray(r.pauseReasons) && r.pauseReasons.every(reason => PAUSE_REASONS.includes(reason)) && [1, 2, 3].includes(Number(r.speed)) && (r.type === "ack" || (r.snapshot !== null && typeof r.snapshot === "object"));
  if (r.type === "receipt") return typeof r.commandId === "string" && Number.isSafeInteger(r.revision) && Boolean(r.receipt && typeof r.receipt === "object");
  if (r.type === "checkpoint") return Boolean(r.checkpoint && typeof r.checkpoint === "object" && typeof (r.checkpoint as CheckpointPayload).serializedCampaign === "string");
  if (r.type === "notification") return typeof r.occurrenceId === "string";
  if (r.type === "failure") return typeof r.message === "string" && typeof r.code === "string" && typeof r.recoverable === "boolean" && Number.isSafeInteger(r.lastGoodRevision);
  return false;
}
